import { useEffect, useState } from 'react'
import { apiClient } from '@/api/client'
import { log } from '@/lib/logger'

/**
 * Story E01.12 — auto-update poller (architecture §13.3, PRD constraint
 * C-08 "NEVER disable auto-update").
 *
 * Polls `GET /api/config/version` every 30 s.
 *  - First load (no `app_build_version` in localStorage):
 *      record the current version silently — no overlay (AC-11).
 *  - Subsequent polls:
 *      if the server reports a different build, surface it via
 *      `pending` so `<AutoUpdateOverlay>` can show the 5-second
 *      countdown + cache-bust reload.
 *
 * The `reload` action is intentionally exposed rather than baked into
 * a `useEffect`: the overlay owns the user-visible countdown UX, this
 * hook just decides when an update is available.
 */
const POLL_MS = 30_000
const STORAGE_KEY = 'app_build_version'

type VersionResponse = { version: string }

export type UseAutoUpdate = {
  /** New version string when an update is available, otherwise null. */
  pending: string | null
  /**
   * Persist the new version, drop service-worker caches (best-effort),
   * and hard-reload with a `?_v=…` query so any intermediate proxy
   * has no excuse to serve stale assets.
   */
  reload: (version: string) => void
}

export function useAutoUpdate(): UseAutoUpdate {
  const [pending, setPending] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    const check = async (): Promise<void> => {
      try {
        const resp = await apiClient.get<VersionResponse>('/config/version')
        const version = resp.data?.version
        if (!version || cancelled) return

        const stored = localStorage.getItem(STORAGE_KEY)
        if (stored === null) {
          // AC-11 — first load. Adopt the server's version silently so the
          // user never sees the overlay just for visiting the app the first
          // time after deployment.
          localStorage.setItem(STORAGE_KEY, version)
          return
        }
        if (stored !== version) {
          setPending(version)
        }
      } catch (err) {
        // Network blips are expected; don't capture as exceptions. The next
        // tick will retry. We log at info-level in dev for debuggability.
        log.info('useAutoUpdate: poll failed', err)
      }
    }

    void check()
    const id = setInterval(check, POLL_MS)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [])

  return {
    pending,
    reload: (version) => {
      try {
        localStorage.setItem(STORAGE_KEY, version)
      } catch {
        // Quota / private-mode failures shouldn't block the reload.
      }
      // Drop cache-storage (PWA caches, fetch caches) — best-effort, async.
      // We don't await: the reload happens regardless, and any leftover
      // entries will be busted by the `?_v=` query string anyway.
      if ('caches' in window) {
        void caches
          .keys()
          .then((keys) => Promise.all(keys.map((k) => caches.delete(k))))
          .catch(() => {})
      }
      const path = window.location.pathname
      window.location.replace(`${path}?_v=${encodeURIComponent(version)}`)
    },
  }
}
